/**
 * Client Statement Page (UI-8, CUSTOMER-STATEMENT.md §7)
 *
 * Accounting view: BE-11 client ledger (real). Customer view: invoices vs money received — dummy (D) until the
 * backend adds `view=customer` (CSB2).
 * Accounting rows link their invoice / receipt; the table columns are shared with the portal statement.
 * Route: /reports/client-ledger/:orgId?from=YYYY-MM-DD&to=YYYY-MM-DD&view=CUSTOMER|ACCOUNTING&lineage=1
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Alert, Skeleton, Card, CardContent, Grid, IconButton, Divider, TextField,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Checkbox, Link,
} from '@mui/material';
import { ArrowBack, PictureAsPdf, TableChart } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, CurrencyText } from '../../components/accounting';
import { formatDate, formatCurrency } from '../../utils/accounting/formatters';
import { clientLedgerService, blobErrorMessage } from '../../services/accounting/clientLedger.service';
import { customerStatementService } from '../../services/statement/customerStatement.service';
import {
  defaultStatementRange, loadStatementPrefs, saveStatementPrefs, truncateRemark,
} from '../../components/statement/statementPrefs';
import { serverMessage } from '../../utils/apiConvert';

export const TYPE_LABEL = {
  OPENING: 'Opening', INVOICE: 'Invoice', PAYMENT: 'Payment', CHEQUE: 'Cheque', CREDIT_NOTE: 'Credit note',
  REFUND: 'Refund paid', INVOICE_VOID: 'Invoice voided', PAYMENT_VOID: 'Payment voided', RECEIPT: 'Receipt',
  RECEIPT_VOID: 'Receipt void', ADJUSTMENT: 'Adjustment',
};
export const CREDIT_TYPES = new Set(['PAYMENT', 'CREDIT_NOTE', 'INVOICE_VOID']);
const STATUS_COLOR = { PAID: 'success.main', PARTIAL: 'warning.main', UNPAID: 'text.secondary', VOID: 'text.disabled' };
const STATUS_LABEL = { PAID: 'Paid', PARTIAL: 'Partial', UNPAID: 'Unpaid', VOID: 'Void' };

const typeText = (r) => (
  <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium, color: r.credit ? 'success.main' : 'info.main' }}>
    {TYPE_LABEL[r.type] || r.type}
  </Typography>
);

// Where a row's reference opens: receipts for receipt rows, the invoice for invoice rows; credit notes carry no invoice id.
const referencePath = (r) => {
  if (!r.referenceId || r.type === 'CREDIT_NOTE') return null;
  if ((r.type === 'PAYMENT' || r.type === 'PAYMENT_VOID') && (r.description || '').startsWith('Receipt ')) return `/receipts/${r.referenceId}`;
  return `/invoices/${r.referenceId}`;
};

const Description = ({ row, pathFor }) => {
  const path = pathFor(row);
  const text = row.description || '';
  const number = row.referenceNumber;
  const at = number ? text.indexOf(number) : -1;
  if (!path || at < 0) return <Typography sx={{ fontSize: fs.sm }}>{text}</Typography>;
  return (
    <Typography sx={{ fontSize: fs.sm }}>
      {text.slice(0, at)}
      <Link component={RouterLink} to={path} underline="hover" onClick={(e) => e.stopPropagation()}>{number}</Link>
      {text.slice(at + number.length)}
    </Typography>
  );
};

// Accounting statement columns; pathFor(row) gives the page a reference number links to (or null).
export const statementColumns = (pathFor) => [
  { key: 'date', label: 'Date', width: '12%', render: (r) => formatDate(r.date) },
  { key: 'description', label: 'Description', width: '30%', render: (r) => <Description row={r} pathFor={pathFor} /> },
  {
    key: 'type', label: 'Type', width: '12%',
    render: (r) => (
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium, color: CREDIT_TYPES.has(r.type) ? 'success.main' : 'info.main' }}>
        {TYPE_LABEL[r.type] || r.type}
      </Typography>
    ),
  },
  { key: 'debit', label: 'Debit', width: '14%', align: 'right', render: (r) => (r.debit ? <CurrencyText amount={r.debit} /> : '—') },
  { key: 'credit', label: 'Credit', width: '14%', align: 'right', render: (r) => (r.credit ? <CurrencyText amount={r.credit} color="success" /> : '—') },
  { key: 'balance', label: 'Balance', width: '14%', align: 'right', render: (r) => <CurrencyText amount={r.balance} bold /> },
];

const ACCOUNTING_COLUMNS = statementColumns(referencePath);

// One lineage item under an invoice: the receipt that paid it, or a credit note against it, oldest first
const lineageText = (l) => {
  const base = `${l.kind === 'CREDIT_NOTE' ? 'Credit note' : 'Receipt'} ${l.number} · ${formatCurrency(l.amount)} · ${formatDate(l.date)}`;
  if (l.kind === 'CREDIT_NOTE') return base;
  return `${base}${l.paymentNumber ? ` · payment ${l.paymentNumber}` : ''}${l.status === 'VOIDED' ? ' (voided)' : ''}`;
};

// A balance in credit (the customer holds an advance) is in brackets, as in the PDF
const BalanceText = ({ amount }) => (Number(amount) < 0
  ? <Typography component="span" sx={{ fontSize: fs.sm, fontWeight: fw.semibold, color: 'success.main', whiteSpace: 'nowrap' }}>({formatCurrency(Math.abs(amount))})</Typography>
  : <CurrencyText amount={amount} bold />);

export const customerColumns = (remarkChars, invoicePath = (id) => `/invoices/${id}`) => [
  { key: 'date', label: 'Date', width: '10%', render: (r) => (r.type === 'OPENING' ? '' : formatDate(r.date)) },
  { key: 'type', label: 'Type', width: '10%', render: typeText },
  {
    key: 'ref', label: 'Ref', width: '14%',
    render: (r) => (['INVOICE', 'INVOICE_VOID'].includes(r.type) && r.referenceId
      ? <Link component={RouterLink} to={invoicePath(r.referenceId)} underline="hover" sx={{ fontSize: fs.sm }}>{r.ref}</Link>
      : r.ref || ''),
  },
  {
    key: 'remarks', label: 'Remarks', width: '21%',
    render: (r) => (
      <Box>
        <Typography sx={{ fontSize: fs.sm, fontStyle: r.type === 'OPENING' ? 'italic' : 'normal' }}>
          {truncateRemark(r.remarks, remarkChars)}
        </Typography>
        {(r.lineage || []).map((l) => (
          <Typography key={`${l.kind}-${l.number}`} sx={{ fontSize: fs.xs, color: l.status === 'VOIDED' ? 'text.disabled' : 'text.secondary' }}>
            ↳ {lineageText(l)}
          </Typography>
        ))}
      </Box>
    ),
  },
  { key: 'debit', label: 'Debit', width: '11%', align: 'right', render: (r) => (r.debit ? <CurrencyText amount={r.debit} /> : '') },
  { key: 'credit', label: 'Credit', width: '11%', align: 'right', render: (r) => (r.credit ? <CurrencyText amount={r.credit} color="success" /> : '') },
  { key: 'balance', label: 'Balance', width: '14%', align: 'right', render: (r) => <BalanceText amount={r.balance} /> },
  {
    key: 'invoiceStatus', label: 'Status', width: '9%',
    render: (r) => (r.invoiceStatus ? (
      <Box>
        <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, color: STATUS_COLOR[r.invoiceStatus] }}>
          {STATUS_LABEL[r.invoiceStatus] || r.invoiceStatus}
        </Typography>
        {Number(r.amountDue) > 0 && r.invoiceStatus !== 'UNPAID' && (
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>due {formatCurrency(r.amountDue)}</Typography>
        )}
      </Box>
    ) : ''),
  },
];

const SummaryRow = ({ label, amount, bold, color, note }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography sx={{ fontSize: fs.sm, fontWeight: bold ? fw.semibold : fw.normal, color: note ? 'text.secondary' : 'text.primary' }}>
      {label}
    </Typography>
    <CurrencyText amount={amount} bold={bold} color={color} />
  </Box>
);

export const CustomerSummary = ({ summary, sx }) => (
  <Paper variant="outlined" sx={{ px: 2, py: 1.5, mt: 1, mb: 1.5, maxWidth: 460, ml: 'auto', ...sx }}>
    <SummaryRow label="Total invoiced" amount={summary.totalInvoiced} />
    <SummaryRow label="Payments received" amount={summary.totalReceived} color="success" />
    {Number(summary.creditNotes) > 0 && <SummaryRow label="Credit notes" amount={summary.creditNotes} color="success" />}
    {Number(summary.invoiceVoids) > 0 && <SummaryRow label="Invoices voided" amount={summary.invoiceVoids} color="success" />}
    {Number(summary.refunds) > 0 && <SummaryRow label="Refunds paid" amount={summary.refunds} />}
    <Divider sx={{ my: 0.5 }} />
    <SummaryRow label="Invoices unpaid (receivable)" amount={summary.receivable} />
    <SummaryRow label="Less: advance in wallet" amount={summary.walletBalance} color="success" />
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>Net balance due</Typography>
      <Box>
        <CurrencyText amount={Math.abs(Number(summary.netBalanceDue))} bold />
        {Number(summary.netBalanceDue) < 0 && (
          <Typography component="span" sx={{ fontSize: fs.xs, color: 'success.main', ml: 0.5 }}>in credit</Typography>
        )}
      </Box>
    </Box>
    {Number(summary.pendingPayments) > 0 && (
      <SummaryRow label="Pending payments (not yet cleared)" amount={summary.pendingPayments} note />
    )}
  </Paper>
);

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
};

const ClientStatement = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefs = loadStatementPrefs();
  const fallback = defaultStatementRange();

  const [range, setRange] = useState({ from: searchParams.get('from') || fallback.from, to: searchParams.get('to') || fallback.to });
  const [view, setView] = useState(searchParams.get('view') || prefs.view);
  const [lineage, setLineage] = useState(searchParams.has('lineage') ? searchParams.get('lineage') === '1' : prefs.lineage);
  const [statement, setStatement] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);

  const isCustomer = view === 'CUSTOMER';

  const loadStatement = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The accounting statement also supplies the organization name, aging and govt fee float.
      const [res, cust] = await Promise.all([
        clientLedgerService.getClientStatement(orgId, range),
        isCustomer ? customerStatementService.getStatement(orgId, { ...range, lineage }) : Promise.resolve(null),
      ]);
      setStatement(res?.data || null);
      setCustomer(cust);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load statement'));
    } finally {
      setLoading(false);
    }
  }, [orgId, range, isCustomer, lineage]);

  useEffect(() => { loadStatement(); }, [loadStatement]);
  useEffect(() => { saveStatementPrefs({ ...loadStatementPrefs(), view, lineage }); }, [view, lineage]);

  const orgName = statement?.organization?.name || 'Client';
  const aging = statement?.aging;
  const govtFloat = statement?.govtFeeFloat;
  const net = Number(statement?.netPosition || 0);

  const handleExport = async (format) => {
    setExporting(format);
    setError(null);
    try {
      const blob = await clientLedgerService.exportStatement(orgId, {
        format, ...range, view: view.toLowerCase(), lineage: isCustomer && lineage, remarkChars: prefs.remarkChars,
      });
      downloadBlob(blob, `Statement-${orgName.replace(/[^a-z0-9]+/gi, '-')}-${range.from}-${range.to}.${format}`);
    } catch (err) {
      setError(await blobErrorMessage(err, 'Could not export the statement.'));
    } finally {
      setExporting(null);
    }
  };

  const inputSx = { width: 150, '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input': { fontSize: fs.sm } };

  return (
    <Box sx={{ p: 0, width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/reports/client-ledger')}>
          <ArrowBack fontSize="small" />
        </IconButton>
        {loading && !statement ? (
          <Skeleton width={200} height={28} />
        ) : (
          <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold, mr: 1 }}>{orgName} — Statement</Typography>
        )}
        <ToggleButtonGroup exclusive size="small" value={view} onChange={(e, v) => v && setView(v)}
          sx={{ '& .MuiToggleButton-root': { height: 32, px: 1.25, fontSize: fs.xs, textTransform: 'none' },
            '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } } }}>
          <ToggleButton value="CUSTOMER">Customer view</ToggleButton>
          <ToggleButton value="ACCOUNTING">Accounting view</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <TextField type="date" size="small" label="From" value={range.from} InputLabelProps={{ shrink: true }} sx={inputSx}
          onChange={(e) => e.target.value && setRange((r) => ({ ...r, from: e.target.value }))} />
        <TextField type="date" size="small" label="To" value={range.to} InputLabelProps={{ shrink: true }} sx={inputSx}
          onChange={(e) => e.target.value && setRange((r) => ({ ...r, to: e.target.value }))} />
        {isCustomer && (
          <FormControlLabel sx={{ mr: 0 }}
            control={<Checkbox size="small" checked={lineage} onChange={(e) => setLineage(e.target.checked)} />}
            label={<Typography sx={{ fontSize: fs.xs }}>Payment lineage</Typography>} />
        )}
        <Button variant="text" size="small" startIcon={<PictureAsPdf />} disabled={!!exporting} onClick={() => handleExport('pdf')}
          sx={{ textTransform: 'none', fontSize: fs.xs }}>
          {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
        </Button>
        <Button variant="text" size="small" startIcon={<TableChart />} disabled={!!exporting} onClick={() => handleExport('csv')}
          sx={{ textTransform: 'none', fontSize: fs.xs }}>
          {exporting === 'csv' ? 'Exporting…' : 'CSV'}
        </Button>
      </Box>

      {isCustomer ? (
        <>
          <DataTable columns={customerColumns(prefs.remarkChars)} data={customer?.lines || []} loading={loading}
            emptyMessage="No transactions in this period." pagination={false} />
          {!loading && customer?.note && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 0.5 }}>{customer.note}</Typography>
          )}
          {!loading && customer && (
            <CustomerSummary summary={customer.summary} />
          )}
        </>
      ) : (
        <>
          {!loading && statement && (
            <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>Opening Balance</Typography>
              <CurrencyText amount={statement.openingBalance} bold />
            </Paper>
          )}
          <DataTable columns={ACCOUNTING_COLUMNS}
            data={(statement?.transactions || []).map((t, i) => ({ ...t, id: `${t.type}-${t.referenceId}-${i}` }))}
            loading={loading} emptyMessage="No transactions in this period." pagination={false} />
          {!loading && statement && (
            <Paper variant="outlined" sx={{ px: 2, py: 1.5, mt: 1, mb: 1.5 }}>
              <SummaryRow label="Closing Balance" amount={statement.closingBalance} bold />
              <SummaryRow label="Wallet Balance" amount={statement.walletBalance} color="success" />
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>Net Position</Typography>
                <Box>
                  <CurrencyText amount={Math.abs(net)} bold />
                  {net < 0 && (
                    <Typography component="span" sx={{ fontSize: fs.xs, color: 'success.main', ml: 0.5 }}>(credit)</Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          )}
        </>
      )}

      {!loading && statement && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                  Aging Buckets
                </Typography>
                {aging && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <AgingRow label="Current" amount={aging.current} />
                    <AgingRow label="31-60 days" amount={aging.thirtyDays} />
                    <AgingRow label="61-90 days" amount={aging.sixtyDays} />
                    <AgingRow label="90+ days" amount={aging.ninetyPlus} warn />
                    <Divider sx={{ my: 0.5 }} />
                    <AgingRow label="Total" amount={aging.total} bold />
                    <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>As of today, by invoice date</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                  Govt Fee Float
                </Typography>
                {govtFloat && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <AgingRow label="Advanced" amount={govtFloat.advanced} />
                    <AgingRow label="Collected" amount={govtFloat.collected} />
                    <AgingRow label="Written off" amount={govtFloat.writtenOff} />
                    <Divider sx={{ my: 0.5 }} />
                    <AgingRow label="Float" amount={govtFloat.float} bold />
                    {Number(govtFloat.float) < 0 && (
                      <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
                        Negative: the customer has paid fees the TC has not paid out yet
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

/** Small row helper for aging / float display */
export const AgingRow = ({ label, amount, bold, warn }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography sx={{ fontSize: fs.sm, fontWeight: bold ? fw.semibold : fw.normal }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: fs.sm,
        fontWeight: bold ? fw.semibold : fw.normal,
        color: warn && Number(amount) > 0 ? 'error.main' : 'text.primary',
      }}
    >
      {formatCurrency(amount)}
    </Typography>
  </Box>
);

export default ClientStatement;
